import {tokens, radii, colors} from '@commercial-helios/ui/theme'
import {graphql} from 'gatsby'
import React from 'react'
import styled from 'styled-components'

import {useCountdown} from '@/hooks/use-countdown'
import useHasMounted from '@/hooks/use-has-mounted'
import {useGlobalStrings} from '@helios/shared/providers/global-strings'
import {Section, container, BackgroundWrapper} from '@helios/shared/styles/grid'
import {Text} from '@helios/shared/styles/typography'
import mq from '@helios/shared/utils/media-query'

type Props = {
    heading: string
    body: string
    dateTime: string
}

export const Countdown = ({heading, body, dateTime}: Props) => {
    const globalStrings = useGlobalStrings()
    const {days, hours, minutes} = useCountdown(dateTime)
    const hasMounted = useHasMounted()

    return (
        <StyledSection>
            <OuterBox color="yellow">
                <TitleBodyWrapper>
                    <Text as="h2" variant="heading-base">
                        {heading}
                    </Text>

                    <Text variant="paragraph-large" color="muted">
                        {body}
                    </Text>
                </TitleBodyWrapper>

                <Timer dateTime={dateTime}>
                    <TimerItem value={hasMounted ? days : 0} label={globalStrings.days} />
                    <TimerItem value={hasMounted ? hours : 0} label={globalStrings.hours} />
                    <TimerItem value={hasMounted ? minutes : 0} label={globalStrings.minutes} />
                </Timer>
            </OuterBox>
        </StyledSection>
    )
}

type TimerItemProps = {
    value: number
    label: string
}

const TimerItem = ({value, label}: TimerItemProps) => {
    return (
        <TimerBox>
            <Text as="span" variant="hero" translate="no">
                {value.toString().padStart(2, '0')}
            </Text>

            <Text as="span" variant="label-base" color="lightMuted">
                {label}
            </Text>
        </TimerBox>
    )
}

const mobileBreakpoint = 'sm'

const StyledSection = styled(Section)`
    ${mq.max(mobileBreakpoint)} {
        padding: 0;
    }
`

const OuterBox = styled(BackgroundWrapper)`
    ${container}

    display: grid;
    row-gap: ${tokens.spacing32};
    grid-template-columns: 1fr;
    padding-top: ${tokens.spacing40};
    padding-bottom: ${tokens.spacing40};
    justify-items: center;
    align-items: center;
    column-gap: ${tokens.spacing24};

    ${mq.min(mobileBreakpoint)} {
        grid-template-columns: 1fr auto;
        border-radius: ${radii.arc20};
    }

    ${mq.min('md')} {
        column-gap: ${tokens.spacing40};
        padding: 80px;
    }

    ${mq.min('xl')} {
        column-gap: 100px;
    }
`

const TitleBodyWrapper = styled.div`
    display: grid;
    row-gap: ${tokens.spacing10};
`

const Timer = styled.time`
    display: grid;
    column-gap: ${tokens.spacing16};
    grid-template-columns: repeat(3, auto);

    ${mq.min(mobileBreakpoint)} {
        column-gap: ${tokens.spacing28};
    }
`

const TimerBox = styled.div`
    display: grid;
    padding: ${tokens.spacing12} ${tokens.spacing8};
    text-transform: capitalize;
    background: ${colors.background.default};
    border-radius: ${radii.arc8};
    box-shadow: ${tokens.shadowRaiseSubtle};
    row-gap: ${tokens.spacing4};
    justify-items: center;

    ${mq.min(mobileBreakpoint)} {
        padding: ${tokens.spacing20} ${tokens.spacing12};
    }
`

export const countdownFragment = graphql`
    fragment Countdown on PrismicSlicePageDataBodyCountdown {
        __typename
        primary {
            # Aliasing needed to get around conflicting fields error (I think conflicting with other slices.. seems like a bug)
            body_text: body
            heading_text: heading
            target_date_time
        }
    }
`
